import React from 'react';
import { Button, Dropdown, Tooltip, message } from 'antd';
import { ShoppingOutlined, CarOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Vehicle } from '../types';
import {
  getEnabledCatalogs,
  openPartsCatalog,
  isVehicleValidForCatalog,
} from '../utils/partsCatalogHelper';
import { PartsCatalogProviderId } from '../types/partsCatalog';

interface PartsCatalogButtonProps {
  /** The vehicle to search parts for */
  vehicle?: Vehicle;

  /** Button size */
  size?: 'small' | 'middle' | 'large';

  /** Button type */
  type?: 'default' | 'primary' | 'dashed' | 'link' | 'text';

  /** Show button text or just icon */
  showText?: boolean;

  /** Custom class name */
  className?: string;
}

/**
 * Parts Catalog Button Component
 * Provides a dropdown menu to open various parts catalogs (RockAuto, etc.)
 * filtered to the specific vehicle
 */
const PartsCatalogButton: React.FC<PartsCatalogButtonProps> = ({
  vehicle,
  size = 'middle',
  type = 'default',
  showText = true,
  className,
}) => {
  const enabledCatalogs = getEnabledCatalogs();
  const isDisabled = !isVehicleValidForCatalog(vehicle);

  const handleCatalogClick = (catalogId: PartsCatalogProviderId) => {
    if (!vehicle) {
      message.warning('Please select a vehicle first');
      return;
    }

    const success = openPartsCatalog(catalogId, vehicle);

    if (success) {
      const catalog = enabledCatalogs.find(c => c.id === catalogId);
      message.success(`Opening ${catalog?.name || 'parts catalog'} for ${vehicle.year} ${vehicle.make} ${vehicle.model}`);
    } else {
      message.error('Failed to open parts catalog');
    }
  };

  // Build dropdown menu items
  const menuItems: MenuProps['items'] = enabledCatalogs.map(catalog => ({
    key: catalog.id,
    label: catalog.name,
    icon: catalog.icon ? <CarOutlined /> : undefined,
    onClick: () => handleCatalogClick(catalog.id as PartsCatalogProviderId),
  }));

  // If only one catalog is enabled, make it a simple button instead of dropdown
  if (enabledCatalogs.length === 1) {
    const catalog = enabledCatalogs[0];

    return (
      <Tooltip
        title={isDisabled
          ? 'Select a vehicle to search parts catalogs'
          : `Search ${catalog.name} for ${vehicle?.year} ${vehicle?.make} ${vehicle?.model}`
        }
      >
        <Button
          type={type}
          size={size}
          icon={<ShoppingOutlined />}
          onClick={() => handleCatalogClick(catalog.id as PartsCatalogProviderId)}
          disabled={isDisabled}
          className={className}
        >
          {showText ? `${catalog.name} Catalog` : undefined}
        </Button>
      </Tooltip>
    );
  }

  // Multiple catalogs - show dropdown
  return (
    <Tooltip
      title={isDisabled
        ? 'Select a vehicle to search parts catalogs'
        : `Search parts catalogs for ${vehicle?.year} ${vehicle?.make} ${vehicle?.model}`
      }
    >
      <Dropdown
        menu={{ items: menuItems }}
        disabled={isDisabled}
        trigger={['click']}
      >
        <Button
          type={type}
          size={size}
          icon={<ShoppingOutlined />}
          disabled={isDisabled}
          className={className}
        >
          {showText ? 'Parts Catalog' : undefined}
        </Button>
      </Dropdown>
    </Tooltip>
  );
};

export default PartsCatalogButton;
